<template>
  <div class="m-2 text-center">
    <b-row>
      <b-col cols="12">
        <div
          v-if="internalLoading"
          class="d-flex justify-content-center align-items-center"
          style="min-height: 250px"
        >
          <b-spinner
            variant="primary"
            label="Loading document..."
          />
        </div>

        <div v-else>
          <div v-if="isImage">
            <b-img
              :src="documentUrl"
              block
              fluid
              rounded
              @load="handleContentLoaded"
              @error="handleContentError"
            />
          </div>

          <div v-else-if="canViewWithGoogleViewer">
            <iframe
              v-if="googleViewerUrl"
              :src="googleViewerUrl"
              width="100%"
              height="600px"
              frameborder="0"
              @load="handleContentLoaded"
              @error="handleContentError"
            />
            <div v-else>
              <b-img
                :src="getFallbackIcon"
                block
                width="250px"
                height="250px"
                rounded
                class="mx-auto"
              />
              <p class="mt-2">
                Unable to display document directly. Please download.
              </p>
            </div>
          </div>

          <div v-else-if="isPdf">
            <object
              :data="documentUrl"
              type="application/pdf"
              width="100%"
              height="600px"
              @load="handleContentLoaded"
              @error="handleContentError"
            >
              <p>
                Your browser doesn't support PDF viewing.
                <a
                  :href="documentUrl"
                  target="_blank"
                >Click here to download the PDF</a>
              </p>
            </object>
          </div>

          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center"
            style="min-height: 250px"
          >
            <p>Unable to display this file type directly.</p>
            <a
              :href="documentUrl"
              target="_blank"
              class="btn btn-primary"
            >
              Download File
            </a>
            <b-img
              v-if="documentUrl"
              :src="getFallbackIcon"
              block
              width="150px"
              height="150px"
              rounded
              class="mt-2 mx-auto"
            />
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BSpinner, BImg,
} from 'bootstrap-vue'
import {
  pdfIcon,
  wordIcon,
  excelIcon,
  csvIcon,
} from '@/helpers/data'

export default {
  name: 'DocumentViewer',
  components: {
    BRow,
    BCol,
    BSpinner,
    BImg,
  },
  props: {
    documentUrl: {
      type: String,
      default: '',
    },
    // External prop to control initial loading state from parent (e.g., fetching URL)
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalLoading: false,
      loadingTimeout: null,
      pdfIcon,
      wordIcon,
      excelIcon,
      csvIcon,
    }
  },
  computed: {
    fileExtension() {
      if (!this.documentUrl) return ''
      try {
        const mimeToExtension = {
          document: 'docx',
          sheet: 'xlsx',
          csv: 'csv',
          pdf: 'pdf',
          jpeg: 'jpg',
          png: 'png',
        }
        const getExtensionFromMimeType = mimeType => mimeToExtension[mimeType] || ''
        const urlObj = new URL(this.documentUrl)
        const documentId = urlObj.searchParams.get('DocumentId')
        if (documentId) {
          const parts = documentId.split('.')
          if (parts.length > 1) {
            // return parts[parts.length - 1].toLowerCase()
            return getExtensionFromMimeType(parts[parts.length - 1]).toLowerCase()
          }
        }
        const pathParts = urlObj.pathname.split('.')
        if (pathParts.length > 1) {
          // return pathParts[pathParts.length - 1].toLowerCase()
          return getExtensionFromMimeType(pathParts[pathParts.length - 1]).toLowerCase()
        }
      } catch (e) {
        console.warn('Invalid documentUrl or error parsing:', e)
      }
      return ''
    },
    isImage() {
      return ['png', 'jpeg', 'jpg', 'bmp', 'gif', 'webp'].includes(
        this.fileExtension,
      )
    },
    isPdf() {
      return this.fileExtension === 'pdf'
    },
    isWordFile() {
      return ['doc', 'docx', 'rtf'].includes(this.fileExtension)
    },
    isExcelFile() {
      return ['xls', 'xlsx'].includes(this.fileExtension)
    },
    isCsvFile() {
      return this.fileExtension === 'csv'
    },
    canViewWithGoogleViewer() {
      return (
        this.isPdf
        // this.isPdf || this.isWordFile || this.isExcelFile || this.isCsvFile
      )
    },
    googleViewerUrl() {
      return this.documentUrl
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(
          this.documentUrl,
        )}&embedded=true`
        : ''
    },
    getFallbackIcon() {
      if (this.isPdf) return this.pdfIcon
      if (this.isWordFile) return this.wordIcon
      if (this.isExcelFile) return this.excelIcon
      if (this.isCsvFile) return this.csvIcon
      return this.defaultDocumentIcon || this.pdfIcon
    },
  },
  watch: {
    documentUrl: {
      immediate: true,
      handler(newUrl) {
        // Clear any existing timeout
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout)
          this.loadingTimeout = null
        }

        if (newUrl) {
          this.internalLoading = true

          // Set a fallback timeout to turn off the spinner
          // in case the @load or @error events don't fire (e.g., cross-origin issues, silent failures).
          // Adjust this duration (e.g., 5000ms = 5 seconds) as appropriate for your expected load times.
          this.loadingTimeout = setTimeout(() => {
            if (this.internalLoading) {
              // Only if still loading
              console.warn('Document loading timed out for:', newUrl)
              this.internalLoading = false
              this.$emit(
                'error',
                'Document loading timed out or could not be displayed directly.',
              )
            }
          }, 5000) // 5 seconds timeout

          // For images, we can preload to trigger load/error events immediately
          if (this.isImage) {
            const img = new Image()
            img.src = newUrl
            img.onload = this.handleContentLoaded
            img.onerror = this.handleContentError
          } else if (
            !this.canViewWithGoogleViewer
            && !this.isImage
            && !this.isPdf
          ) {
            this.internalLoading = false
            this.$emit('loaded') // Assume it's "loaded" if it's just a download link
          }
          // For PDF (object tag) and Google Viewer iframes, their @load/@error handlers will turn off the spinner
        } else {
          // If URL is cleared, stop loading and clear timeout
          this.internalLoading = false
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout)
            this.loadingTimeout = null
          }
        }
      },
    },
    loading: {
      immediate: true,
      handler(newLoading) {
        if (newLoading && !this.documentUrl) {
          this.internalLoading = true
        } else if (!newLoading && !this.documentUrl) {
          this.internalLoading = false
        }
      },
    },
  },
  beforeUnmount() {
    // Clear timeout if component is destroyed while loading
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
      this.loadingTimeout = null
    }
  },
  methods: {
    handleContentLoaded() {
      if (this.loadingTimeout) {
        // Clear timeout on successful load
        clearTimeout(this.loadingTimeout)
        this.loadingTimeout = null
      }
      this.internalLoading = false
      this.$emit('loaded')
    },
    handleContentError(event) {
      if (this.loadingTimeout) {
        // Clear timeout on error
        clearTimeout(this.loadingTimeout)
        this.loadingTimeout = null
      }
      this.internalLoading = false
      this.$emit('error', event) // Pass the event for more details if parent needs it
    },
  },
}
</script>

<style scoped>
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>
